import { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Information } from '@carbon/react/icons';
import {
  MenuButton,
  MenuItem,
  Toggletip,
  ToggletipButton,
  ToggletipContent,
} from '@carbon/react';
import { PageHeader } from 'components/page-header';
import {
  ActivityFeedPanel,
  ActivityFeedPanelOpenButton,
} from 'components/activity-feed-panel';
import {
  AddDocumentSidePanel,
  DocumentUploadConfig,
} from 'components/add-document-modal/add-document-side-panel';
import routes from '~/routes';
import AfeCatalogContent from './afe-catalog-content';
import styles from './page.module.scss';

const AFE_UPLOAD_CONFIG: DocumentUploadConfig = {
  documentType: 'afe',
  promptCategory: 'afe_document_fields',
  accept: ['.pdf', '.tif', '.tiff'],
  uploadHelperText: 'Upload a single PDF or TIFF file (max 500 MB limit)',
  promptHelperText: 'For AFE documents',
  idPrefix: 'afe-',
  errorFallback: 'Problem uploading AFE document',
};

/**
 * Top-level AFE Catalog page. Renders the page header with title,
 * Activity drawer trigger, an info tooltip, and the "Add AFE" menu, followed by
 * the content orchestrator. "Add AFE" offers two paths: AFE Document (upload a
 * source document to auto-provision and auto-connect) and AFE Wizard (manual,
 * step-by-step creation with no matching). The Activity drawer is cross-record
 * (no `modelId`) and read-only (comments suppressed via empty `commentsHref`).
 * @return {JSX.Element} The AFE Catalog page.
 */
const AfeCatalogPage: FC = () => {
  const history = useHistory();
  const [isDocumentPanelOpen, setIsDocumentPanelOpen] = useState(false);

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title="AFE Catalog"
        actions={
          <div className={styles.headerActions}>
            <ActivityFeedPanelOpenButton buttonSize="lg" />
            <Toggletip align="bottom-end">
              <ToggletipButton label="About the Add AFE paths">
                <Information />
              </ToggletipButton>
              <ToggletipContent>
                <h6 className={styles.tooltipHeading}>
                  AFE Action Descriptions
                </h6>
                <p className={styles.tooltipParagraph}>
                  <strong>AFE Document</strong> — uploads a source AFE document
                  for full structuring. Runs Entity and Well Matching review
                  before the record is created. The AFE is automatically
                  connected to the AFE Catalog with{' '}
                  <strong>AFE Input Source</strong> marked as{' '}
                  <strong>Auto-Connected</strong>.
                </p>
                <p className={styles.tooltipParagraph}>
                  <strong>AFE Wizard</strong> — manually creates an AFE step by
                  step without a source document. Each step requires the user to
                  fill in all AFE information manually. Matching agents are not
                  run and the record enters the catalog without resolved
                  entities or wells. These can be manually resolved later.
                </p>
              </ToggletipContent>
            </Toggletip>
            <MenuButton label="Add AFE" kind="primary" size="lg">
              <MenuItem
                label="AFE Document"
                onClick={() => setIsDocumentPanelOpen(true)}
              />
              <MenuItem
                label="AFE Wizard"
                onClick={() => history.push(routes.afeCatalogNewPath)}
              />
            </MenuButton>
          </div>
        }
      />
      <AfeCatalogContent />
      <AddDocumentSidePanel
        open={isDocumentPanelOpen}
        onClose={() => setIsDocumentPanelOpen(false)}
        title="Add AFE Document"
        subtitle="Upload a source AFE document to begin processing."
        config={AFE_UPLOAD_CONFIG}
      />
      <ActivityFeedPanel
        modelType="AfeHeader"
        title="Activity"
        subtitle="AFE Catalog"
        commentsHref=""
      />
    </>
  );
};

export { AfeCatalogPage };
